/**
 * convertOldURLToNewURL
 *
 * A pure, side-effect-free utility that converts an old application URL
 * query string into the corresponding new application pathname + query string.
 *
 * No React, Redux, or hook dependencies.
 */

import { DateTime } from "luxon";
import {
  BRANDS,
  BUBBLE,
  BARCODE,
  BUTTERLFY,
  DEFAULT,
  DIFFERENTIATION,
  DONUT,
  FILTERS,
  FUNNEL,
  MARKETS,
  PERCEPTUAL,
  POINT_IN_TIME,
  PRE_POST,
  DISTRIBUTION,
  QUESTION_OPTIONS,
  SECONDARY_BRANDS,
  SEGMENTS,
  TREND,
  URL_BY_BRAND,
  URL_BY_MARKET,
  URL_BY_SEGMENT,
} from "@/constant/constantsThrougoutProject";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import type { MarketAdapterType } from "@/store/slices/markets/marketsSlice";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface NewURLResult {
  /** New pathname, e.g. "/equity/by-brand" */
  pathname: string;
  /** New query string without the leading "?", e.g. "markets=1&brands=2" */
  url: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PERIOD_KEY_MAP: Record<string, string> = {
  "P-W": "P_W",
  "P-M": "P_M",
  "P-Q": "P_Q",
  "P-Y": "P_Y",
  "MV-1": "MV_1",
  "MV-2": "MV_2",
  "MV-4": "MV_4",
  "MV-8": "MV_8",
  "MV-12": "MV_12",
};

const PROFILE_METRIC_TO_PATH: Record<string, string> = {
  conversionfunnel: "conversion",
  equity: "equity",
  personification: "emotional",
  advertising: "advertising",
};

const GROUP_TO_PATH: Record<string, string> = {
  conversion: "conversion",
  equity: "equity",
  emotional: "emotional",
  advertising: "advertising",
};

/**
 * Exhaustive list of all known legacy (old-application) pathnames.
 * Kept here so the function is fully self-contained — no dependency
 * on oldUtils.ts, which will be deleted once migration is complete.
 */
const LEGACY_PATHNAMES = new Set<string>([
  "/segments/equity",
  "/segments/advertising",
  "/segments/conversion",
  "/segments/emotional",
  "/segments/trends",
  "/segments/profile",
  "/segments/reputation",
  "/segments/visibility",
  "/competitive/equity",
  "/competitive/advertising",
  "/competitive/conversion",
  "/competitive/emotional",
  "/competitive/trends",
  "/competitive/profile",
  "/competitive/reputation",
  "/competitive/visibility",
  "/global/equity",
  "/global/advertising",
  "/global/conversion",
  "/global/emotional",
  "/global/trends",
  "/global/profile",
  "/global/reputation",
  "/global/visibility",
  "/advanced/butterfly",
  "/advanced/prepost",
  "/advanced/perceptual",
  "/advanced/differentiation",
  "/advanced/reputation",
  "/advanced/visibility",
  "/questions/segment",
  "/questions/brand",
]);

// Array keys that must be repeated via append(), not set()
const ARRAY_PARAM_KEYS = new Set([
  MARKETS,
  BRANDS,
  FILTERS,
  SECONDARY_BRANDS,
  SEGMENTS,
  QUESTION_OPTIONS,
]);

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Returns the new sub-path segment based on the old module name. */
function resolveSubPath(module: string): string {
  const m = module?.toLowerCase();
  if (m === "segments" || m === "detailed") return URL_BY_SEGMENT;
  if (m === "global") return URL_BY_MARKET;
  return URL_BY_BRAND;
}

/** Parses old-format URLSearchParams into a typed record. */
function parseOldParams(
  qs: URLSearchParams,
): Record<string, string | number | string[] | number[] | (string | number)[]> {
  const result: Record<string, string | number | string[] | number[] | (string | number)[]> = {};

  for (const [key] of qs.entries()) {
    if (key === "questionOptions" || key === "market" || key === "markets") {
      // Market values are identity strings (e.g. "US") — keep as strings
      result[key] = qs.getAll(key);
    } else if (key === "brands" || key === "brand" || key === "secondaryBrands") {
      // Brand IDs can be numeric (regular brands) or ULID strings (brand sets)
      result[key] = qs.getAll(key).map((val) => {
        const num = Number(val);
        return isNaN(num) ? val : num;
      });
    } else if (key === "segments" || key === "filters") {
      // Segment IDs can be numeric or ULID strings — preserve both
      result[key] = qs.getAll(key).map((val) => {
        const num = Number(val);
        return isNaN(num) ? val : num;
      });
    } else {
      result[key] = qs.get(key) as string;
    }
  }

  return result;
}

interface PathnameDetail {
  pathname: string;
  mode: string;
  visualizationType: string;
}

/**
 * Maps a screen name (lowercase) to the V2 POINT_IN_TIME primary chart type.
 * Mirrors `POINT_IN_TIME_VIZ_BY_MODULE` used by the chart layer.
 */
const SCREEN_DEFAULT_VIZ: Readonly<Record<string, string>> = {
  equity: DONUT,
  advertising: DONUT,
  reputation: DONUT,
  conversion: FUNNEL,
  emotional: BUBBLE,
  visibility: BARCODE,
  // questions / exclusivequestions handled separately (BAR)
};

/** Derives the new pathname, mode, and visualizationType from old params. */
function resolvePathnameDetail(params: {
  module: string;
  screenType: string;
  group: string;
  metric: string;
  mode: string;
  chartType: string;
  visualizationType: string;
}): PathnameDetail {
  const { module, screenType, group, metric, mode, chartType, visualizationType } = params;
  const moduleLower = module?.toLowerCase();
  const screenLower = screenType?.toLowerCase();

  if (screenType === "TRENDS") {
    return {
      pathname: `/${group?.toLowerCase()}/${resolveSubPath(module)}`,
      mode: TREND,
      visualizationType: DEFAULT,
    };
  }

  if (screenType === "PROFILE") {
    const path = PROFILE_METRIC_TO_PATH[metric?.toLowerCase()] ?? "equity";
    return {
      pathname: `/${path}/${resolveSubPath(module)}`,
      mode: POINT_IN_TIME,
      visualizationType: DISTRIBUTION,
    };
  }

  if (screenType === "REPUTATION" || screenType === "VISIBILITY") {
    const subPath =
      moduleLower === "detailed"
        ? URL_BY_SEGMENT
        : moduleLower === "global"
          ? URL_BY_MARKET
          : URL_BY_BRAND;
    // Use the V2 module-specific default when the old URL carried "DEFAULT"
    // or had no visualizationType at all.
    const vizType =
      visualizationType && visualizationType !== DEFAULT
        ? visualizationType
        : SCREEN_DEFAULT_VIZ[screenLower] ?? DEFAULT;
    return {
      pathname: `/${screenLower}/${subPath}`,
      mode: mode ?? POINT_IN_TIME,
      visualizationType: vizType,
    };
  }

  if (moduleLower === "advanced") {
    if (screenLower === "prepost" || screenLower === "pre_post") {
      const path = GROUP_TO_PATH[group?.toLowerCase()] ?? "equity";
      return { pathname: `/${path}/${URL_BY_BRAND}`, mode: PRE_POST, visualizationType: DEFAULT };
    }

    const vizMap: Record<string, string> = {
      [PERCEPTUAL.toLowerCase()]: PERCEPTUAL,
      [BUTTERLFY.toLowerCase()]: BUTTERLFY,
      [DIFFERENTIATION.toLowerCase()]: DIFFERENTIATION,
    };
    const vizType = vizMap[screenLower];
    if (vizType) {
      return { pathname: `/emotional/${URL_BY_BRAND}`, mode: POINT_IN_TIME, visualizationType: vizType };
    }
  }

  if (moduleLower === "questions") {
    const subPath = screenType === "SEGMENT" ? URL_BY_SEGMENT : URL_BY_BRAND;
    return {
      pathname: `/exclusivequestions/${subPath}`,
      mode,
      visualizationType:
        visualizationType === "TABLE" ? "TABLE" : mode === TREND ? "LINE" : "BAR",
    };
  }

  if (
    moduleLower === "segments" ||
    moduleLower === "competitive" ||
    moduleLower === "global" ||
    moduleLower === "detailed"
  ) {
    // Preserve the visualizationType from the old URL when it is meaningful
    // (i.e. not the generic "DEFAULT" placeholder and not empty).  This lets
    // a bookmarked URL that carried e.g. visualizationType=BAR land on the
    // correct chart type instead of always resetting to the screen default.
    // chartType=SECONDARY always wins (it maps to a specific BAR layout).
    // When the old URL had no specific type, use the V2 module-specific default
    // (DONUT for equity/advertising, FUNNEL for conversion, BUBBLE for emotional).
    const resolvedVizType =
      chartType === "SECONDARY"
        ? "BAR"
        : visualizationType && visualizationType !== DEFAULT
          ? visualizationType
          : SCREEN_DEFAULT_VIZ[screenLower] ?? DEFAULT;
    return {
      pathname: `/${screenLower}/${resolveSubPath(module)}`,
      mode: POINT_IN_TIME,
      visualizationType: resolvedVizType,
    };
  }

  // Fallback
  return { pathname: `/equity/${URL_BY_BRAND}`, mode: POINT_IN_TIME, visualizationType: DEFAULT };
}

/** Converts a compact date string (yyyyMMdd) to ISO format (yyyy-MM-dd). */
function normaliseDate(value: string): string | undefined {
  if (value?.length === 8) {
    return DateTime.fromFormat(value, "yyyyMMdd").toFormat("yyyy-MM-dd");
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns `true` when the given URL (or pathname) belongs to the old legacy
 * application, `false` otherwise.
 *
 * Accepts any of:
 *  - a full URL:       `"https://example.com/segments/equity?brands=1"`
 *  - an absolute path: `"/competitive/trends?brands=1"`
 *  - a bare pathname:  `"/advanced/butterfly"`
 *
 * Only the pathname portion is used for matching — query strings and hashes
 * are ignored.
 *
 * @example
 * ```ts
 * isLegacyURL("/segments/equity?brands=1")   // → true
 * isLegacyURL("/equity/by-brand?brands=1")   // → false
 * ```
 */
export function isLegacyURL(url: string): boolean {
  if (!url) return false;

  let pathname: string;

  try {
    // Works for full URLs ("https://…") as well as protocol-relative paths.
    // For bare pathnames, the URL constructor requires a base.
    pathname = new URL(url, "http://x").pathname;
  } catch {
    // Should never happen with the base provided, but fall back gracefully.
    pathname = url.split("?")[0].split("#")[0];
  }

  // Normalise: remove trailing slash so "/segments/equity/" also matches.
  const normalisedPathname = pathname.replace(/\/+$/, "");

  return LEGACY_PATHNAMES.has(normalisedPathname);
}

/**
 * Converts an old application URL query string to a new-format URL.
 *
 * @param oldQueryString  The raw query string from the old URL (part after "?").
 * @param markets         Normalised market entities from the Redux store —
 *                        used to translate old market identity strings → numeric IDs.
 * @param legacyPathname  Optional: the legacy pathname (e.g. "/competitive/conversion").
 *                        When `module` / `screenType` are absent from the query string
 *                        (bookmarked URLs that only carried the path), they are derived
 *                        from the first two path segments as a fallback.
 * @returns               `{ pathname, url }` ready to pass to `navigate()`.
 *
 * @example
 * ```ts
 * const { pathname, url } = convertOldURLToNewURL(
 *   "module=competitive&screenType=equity&brands=1",
 *   marketsFromStore,
 *   "/competitive/equity",
 * );
 * navigate({ pathname, search: url });
 * ```
 */
export function convertOldURLToNewURL(
  oldQueryString: string,
  markets: MarketAdapterType,
  legacyPathname?: string,
): NewURLResult {
  const parsed = parseOldParams(new URLSearchParams(oldQueryString));

  // ── Fallback: derive module / screenType from the legacy pathname ──────────
  // Some bookmarked URLs only carry the path (e.g. /competitive/conversion)
  // without repeating module= and screenType= in the query string.  In that
  // case the query-string parser leaves both fields undefined, so we derive
  // them from the first two path segments of the legacy pathname.
  if (legacyPathname && (!parsed.module || !parsed.screenType)) {
    const segments = legacyPathname.replace(/\/+$/, "").split("/");
    // segments[0] is "" (leading slash), segments[1] is module, segments[2] is screenType
    if (!parsed.module && segments[1]) {
      parsed.module = segments[1]; // e.g. "competitive"
    }
    if (!parsed.screenType && segments[2]) {
      parsed.screenType = segments[2].toUpperCase(); // e.g. "CONVERSION"
    }
  }

  // ── Advanced pathname override ────────────────────────────────────────────
  // /advanced/butterfly, /advanced/differentiation, /advanced/perceptual and
  // /advanced/prepost sometimes carry a stale or incorrect `screenType` in the
  // query string (e.g. "TRENDS").  For the advanced module the pathname is the
  // authoritative source — always override module + screenType from it.
  if (legacyPathname) {
    const pathSegs = legacyPathname.replace(/\/+$/, "").split("/");
    const legacyModule = pathSegs[1]?.toLowerCase(); // e.g. "advanced"
    const legacyScreen = pathSegs[2]?.toUpperCase(); // e.g. "BUTTERFLY"
    if (legacyModule === "advanced" && legacyScreen) {
      parsed.module = "advanced";
      parsed.screenType = legacyScreen; // e.g. "BUTTERFLY" | "PREPOST" | "PERCEPTUAL" | "DIFFERENTIATION"
    }
  }

  // ── Trends pathname override ──────────────────────────────────────────────
  // In the old app, /competitive/trends (and /segments/trends, /global/trends)
  // stored the *metric* screen type (e.g. EMOTIONAL, EQUITY) in `screenType`
  // rather than "TRENDS".  When the legacy pathname ends with "/trends" but
  // the query-string `screenType` is a metric name (not "TRENDS"), we:
  //   1. Promote `screenType` → `group` so resolvePathnameDetail can derive
  //      the correct new pathname (e.g. /emotional/brand).
  //   2. Override `module` with the pathname module (e.g. "competitive") so
  //      resolveSubPath returns the correct sub-path (brand / segment / market).
  //   3. Set `screenType = "TRENDS"` to trigger the TREND mode branch.
  if (legacyPathname) {
    const pathSegs = legacyPathname.replace(/\/+$/, "").split("/");
    const legacyScreen = pathSegs[2]?.toUpperCase(); // e.g. "TRENDS"
    if (legacyScreen === "TRENDS" && parsed.screenType !== "TRENDS") {
      // screenType holds the metric (e.g. "EMOTIONAL") — promote to group
      // only when group is absent or the literal string "null".
      if (!parsed.group || parsed.group === "null") {
        parsed.group = parsed.screenType as string; // e.g. "EMOTIONAL"
      }
      // Use the pathname module so resolveSubPath gives the correct sub-path:
      //   competitive → brand, segments → segment, global → market
      parsed.module = pathSegs[1]; // e.g. "competitive"
      parsed.screenType = "TRENDS";
    }
  }

  if (!parsed.visualizationType) {
    parsed.visualizationType = "DEFAULT";
  }

  const detail = resolvePathnameDetail({
    module: parsed.module as string,
    screenType: parsed.screenType as string,
    group: parsed.group as string,
    metric: parsed.metric as string,
    visualizationType: (
      parsed.visualizationType === "STACKED_BAR" ? "DISTRIBUTION" : parsed.visualizationType
    ) as string,
    mode: ((parsed.viewMode || parsed.mode) as string),
    chartType: parsed.chartType as string,
  });

  // ---- Build the final params record -----------------------------------

  const final: Record<string, any> = {};

  for (const [key, value] of Object.entries(parsed)) {
    switch (key) {
      case "audienceType":
        final.base = value;
        break;

      case "statTest":
        if (["POP", "PTP", "YOY", "NONE"].includes(value as string)) {
          final.statTest = value;
        } else if (value === "true") {
          final.statTest = "POP";
        } else {
          final.statTest = "NONE";
        }
        break;

      case "periodType":
      case "postPeriodType":
        final[key] = value;
        break;

      case "periodStartDate":
      case "periodEndDate":
      case "postStartDate":
      case "postEndDate": {
        const iso = normaliseDate(value as string);
        if (iso) final[key] = iso;
        break;
      }

      case "intervalType":
        final.period = PERIOD_KEY_MAP[value as string] ?? value;
        break;

      case "market":
      case "markets":
        final.markets = (value as string[])
          .map((identity) =>
            markets?.ids?.find(
              (id: number) => markets?.entities?.[id]?.identity === identity,
            ) ?? null,
          )
          .filter((id) => id !== null);
        break;

      case "visualizationType":
        final.visualizationType = detail.visualizationType;
        break;

      case "brand":
      case "brands": {
        const viz = detail.visualizationType;
        if (viz === DIFFERENTIATION || viz === BUTTERLFY) {
          final.brands = (value as any[]).slice(0, 1);
          final.secondaryBrands = (value as any[]).slice(1);
        } else {
          final.brands = value;
        }
        break;
      }

      case "segments":
      case "filters":
      case "questionOptions":
        final[key] = value;
        break;

      case "metric":
        if (detail.mode === TREND) {
          final.trendsMetric = (value as string)?.toLowerCase().replace(/[^a-z0-9]/g, "");
        } else if (detail.visualizationType === DISTRIBUTION) {
          final.profileMetric = value;
        }
        break;

      case "response":
        // TREND: the legacy `response` token encoded a one-axis scoring slug
        // that no longer maps onto anything — the scoring pair now falls to
        // the selection listener's catalog-driven defaults. Metric translation
        // is kept (handled by the `metric` case above).
        if (detail.visualizationType === DISTRIBUTION) {
          final.profileResponse = value;
        }
        break;

      case "group":
        if (detail.visualizationType === DISTRIBUTION) {
          final.profileGroup = value;
        }
        break;

      case "viewId":
      case "groupedBy":
      case "scoreType":
        final[key] = value;
        break;

      default:
        break;
    }
  }

  final.mode = detail.mode;

  // ---- Serialise to URLSearchParams ------------------------------------

  const pathConfig = ROUTES_CHILDREN_LOOKUP[detail.pathname];
  let mode = typeof detail.mode === "string" ? detail.mode : POINT_IN_TIME;

  if (
    mode === POINT_IN_TIME &&
    [PERCEPTUAL, BUTTERLFY, DIFFERENTIATION, DISTRIBUTION].includes(detail.visualizationType)
  ) {
    mode = detail.visualizationType;
  }

  const modeKey =
    (pathConfig?.modeOptions?.includes(mode) && mode) || POINT_IN_TIME;

  const urlParams = new URLSearchParams();
  pathConfig?.urlKeysMapping?.[modeKey]?.forEach((key: string) => {
    if (ARRAY_PARAM_KEYS.has(key)) {
      (final[key] as any[] | undefined)?.forEach((val: any) =>
        urlParams.append(key, val),
      );
    } else if (final[key] != null && final[key] !== "") {
      urlParams.append(key, final[key]);
    }
  });

  return { pathname: detail.pathname, url: urlParams.toString() };
}
